import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Flame, Sparkles, Percent, Euro, Loader2, ArrowUpRight, Send, Bookmark,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import ScoreBadge from "@/components/ScoreBadge";
import { Button } from "@/components/ui/button";
import { fetchStats, scoreAll } from "@/lib/api";

const COLORS = ["#0071E3", "#A259FF", "#FF62A5", "#FF8A3D", "#34C759", "#FF9500"];

export default function Overview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);

  const load = async () => {
    try {
      setStats(await fetchStats());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleScoreAll = async () => {
    setScoring(true);
    try {
      const res = await scoreAll();
      toast.success(`${res.scored} Deals von der KI bewertet`);
      await load();
    } catch (e) {
      toast.error("KI-Bewertung fehlgeschlagen");
    } finally {
      setScoring(false);
    }
  };

  const maxCat = stats ? Math.max(...stats.by_category.map((c) => c.count), 1) : 1;

  return (
    <div data-testid="overview-page">
      <PageHeader title="Übersicht" subtitle="Dein automatisierter Deal-Hub auf einen Blick">
        <Button
          onClick={handleScoreAll}
          disabled={scoring}
          className="rounded-full ai-gradient-bg text-white hover:opacity-90 active:scale-95 transition-all"
          data-testid="score-all-button"
        >
          {scoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Alle KI-bewerten
        </Button>
      </PageHeader>

      <div className="px-6 md:px-10 py-8">
        {loading || !stats ? (
          <div className="flex items-center justify-center py-32 text-[#86868B]">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6">
              <KpiCard testid="kpi-total" label="Aktive Deals" value={stats.total_deals} icon={Flame} accent="#FF8A3D" delay={0} />
              <KpiCard testid="kpi-score" label="Ø KI-Score" value={stats.avg_score} suffix="/100" icon={Sparkles} accent="#A259FF" delay={0.06} />
              <KpiCard testid="kpi-discount" label="Ø Rabatt" value={stats.avg_discount} suffix="%" icon={Percent} accent="#FF3B30" delay={0.12} />
              <KpiCard testid="kpi-commission" label="Provisions-Pool" value={stats.est_commission.toFixed(0)} suffix="€" icon={Euro} accent="#34C759" delay={0.18} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
              {/* Top deals */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="xl:col-span-2 bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-bold text-[#1D1D1F]">Top KI-Deals</h2>
                  <Button variant="ghost" size="sm" onClick={() => navigate("/admin/deals")} className="rounded-full text-[#0071E3] text-sm" data-testid="see-all-deals">
                    Alle ansehen <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </div>

                {stats.top_deals.length === 0 ? (
                  <div className="text-center py-10">
                    <img src="https://static.prod-images.emergentagent.com/jobs/4ca59440-3756-4a33-9153-174842ff835f/images/f3df7140de241ba362f5b7d787522201e3bdc4c710a971bcf75cccc27d55c611.png" alt="Keine Deals" className="max-h-40 mx-auto mb-3 opacity-90" />
                    <p className="text-sm text-[#86868B]">Noch keine bewerteten Deals. Klicke auf „Alle KI-bewerten“.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-black/5">
                    {stats.top_deals.map((d, i) => (
                      <div key={d.id} className="flex items-center gap-3 py-3 group" data-testid={`top-deal-${i}`}>
                        <span className="font-heading font-bold text-[#A1A1A6] w-5 text-sm">{i + 1}</span>
                        <img src={d.image_url || "/wizard-icon.png"} alt="" className="h-11 w-11 rounded-xl object-cover bg-[#F5F5F7]" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#1D1D1F] truncate">{d.product_name}</p>
                          <p className="text-xs text-[#86868B]">{d.source} · -{d.discount_percent}% · {d.deal_price.toFixed(2)} €</p>
                        </div>
                        <ScoreBadge score={d.ai_score} />
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>

              {/* Category distribution + status */}
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.26 }}
                className="bg-white rounded-2xl border border-black/5 shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-6"
              >
                <h2 className="font-heading text-lg font-bold text-[#1D1D1F] mb-4">Kategorien</h2>
                <div className="space-y-3 mb-6">
                  {stats.by_category.map((c, i) => (
                    <div key={c.category}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#1D1D1F] font-medium">{c.category}</span>
                        <span className="text-[#86868B]">{c.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#F5F5F7] overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(c.count / maxCat) * 100}%` }}
                          transition={{ delay: 0.3 + i * 0.05, type: "spring", stiffness: 120, damping: 20 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: COLORS[i % COLORS.length] }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-violet-50 p-3">
                    <Bookmark className="h-4 w-4 text-violet-600 mb-1.5" strokeWidth={2} />
                    <p className="font-heading text-xl font-bold text-[#1D1D1F]">{stats.saved}</p>
                    <p className="text-xs text-[#86868B]">Gespeichert</p>
                  </div>
                  <div className="rounded-xl bg-blue-50 p-3">
                    <Send className="h-4 w-4 text-blue-600 mb-1.5" strokeWidth={2} />
                    <p className="font-heading text-xl font-bold text-[#1D1D1F]">{stats.posted}</p>
                    <p className="text-xs text-[#86868B]">Gepostet</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
